import moment from 'moment'
import { default as axios } from './axiosConfig'
import { getEnv } from '@utils/env'
import {
  NotificationCategory,
  NotificationPriority,
  type BackendNotification,
  type Notification,
  type NotificationCounts,
  type NotificationTriggerType,
} from '@dataTypes/notification.types'

const BUSINESS_LAYER_NOTIFICATIONS = getEnv(
  'REACT_APP_BUSINESS_LAYER_NOTIFICATIONS',
)

const headers = { 'Content-Type': 'application/json' }

export interface ListNotificationsParams {
  date_from?: string
  date_to?: string
  priority?: NotificationPriority
  category?: NotificationCategory
  status?: 'read' | 'unread'
  search?: string
  patient_id?: string
  limit?: number
  offset?: number
}

interface BackendListResponse {
  statusCode: number
  message: string
  data: { items: BackendNotification[] }
}

interface BackendCountsResponse {
  statusCode: number
  message: string
  data: { counts: NotificationCounts }
}

interface BackendSingleResponse {
  statusCode: number
  message: string
  data: { notification: BackendNotification | null }
}

/**
 * Maps the backend `BackendNotification` shape (camelCase, ISO timestamps) onto the
 * UI-friendly `Notification` shape used by the existing Notifications page so the
 * component does not need to know about both representations.
 */
export const mapBackendNotification = (
  n: BackendNotification,
): Notification => {
  const created = moment(n.createdAt)
  const date = created.isValid()
    ? created.format('MMMM D, YYYY')
    : moment().format('MMMM D, YYYY')
  const time = created.isValid() ? created.format('h:mm A') : ''
  return {
    id: n.notificationId,
    notification_id: n.notificationId,
    priority: (n.priority as NotificationPriority) ?? NotificationPriority.LOW,
    title: n.title,
    date,
    time,
    message: n.message,
    actionButtonText: n.actionButtonText ?? '',
    patient_id: n.patientId ?? undefined,
    category: (n.category as NotificationCategory) ?? undefined,
    type: n.type as NotificationTriggerType | string,
    clinic_id: n.clinicId,
    action_url: n.actionUrl,
    action_metadata: n.actionMetadata,
    source_module: n.sourceModule,
    source_record_id: n.sourceRecordId,
    is_read: Boolean(n.isRead),
    dismissed: Boolean(n.dismissed),
    created_at: n.createdAt,
  }
}

const buildHeaders = (): Record<string, string> => {
  const result: Record<string, string> = { ...headers }
  const clinicId = localStorage.getItem('clinic_id')
  const dbName = localStorage.getItem('db_name')
  if (clinicId) result['X-Clinic-ID'] = clinicId
  if (dbName) result['X-Db-Name'] = dbName
  const tenantId = localStorage.getItem('tenant_id')
  if (tenantId) result['X-Tenant-ID'] = tenantId
  return result
}

const buildClinicIdParams = (): Record<string, string> => {
  const clinicId = localStorage.getItem('clinic_id')
  return clinicId ? { clinic_id: clinicId } : {}
}

export const getNotifications = async (
  params: ListNotificationsParams = {},
): Promise<Notification[]> => {
  const { data } = await axios.get<BackendListResponse>(
    `${BUSINESS_LAYER_NOTIFICATIONS}/api/v1/notifications`,
    {
      headers: buildHeaders(),
      params: {
        ...buildClinicIdParams(),
        ...params,
      },
    },
  )
  const items = data?.data?.items ?? []
  return items.map(mapBackendNotification)
}

export const getNotificationCounts = async (params?: {
  date_from?: string
  date_to?: string
  patient_id?: string
}): Promise<NotificationCounts> => {
  const { data } = await axios.get<BackendCountsResponse>(
    `${BUSINESS_LAYER_NOTIFICATIONS}/api/v1/notifications/counts`,
    {
      headers: buildHeaders(),
      params: {
        ...buildClinicIdParams(),
        ...(params ?? {}),
      },
    },
  )
  return (
    data?.data?.counts ?? {
      critical: 0,
      urgent: 0,
      low: 0,
      total: 0,
      unread: 0,
    }
  )
}

export const getNotificationById = async (
  notificationId: string,
): Promise<Notification | null> => {
  const { data } = await axios.get<BackendSingleResponse>(
    `${BUSINESS_LAYER_NOTIFICATIONS}/api/v1/notifications/${notificationId}`,
    { headers: buildHeaders() },
  )
  return data?.data?.notification
    ? mapBackendNotification(data.data.notification)
    : null
}

export const markNotificationRead = async (
  notificationId: string,
): Promise<void> => {
  await axios.patch(
    `${BUSINESS_LAYER_NOTIFICATIONS}/api/v1/notifications/${notificationId}/read`,
    {},
    { headers: buildHeaders() },
  )
}

export const markNotificationUnread = async (
  notificationId: string,
): Promise<void> => {
  await axios.patch(
    `${BUSINESS_LAYER_NOTIFICATIONS}/api/v1/notifications/${notificationId}/unread`,
    {},
    { headers: buildHeaders() },
  )
}

export const dismissNotification = async (
  notificationId: string,
): Promise<void> => {
  await axios.patch(
    `${BUSINESS_LAYER_NOTIFICATIONS}/api/v1/notifications/${notificationId}/dismiss`,
    {},
    { headers: buildHeaders() },
  )
}

export const markAllNotificationsRead = async (): Promise<{
  updated: number
}> => {
  const { data } = await axios.patch<{
    statusCode: number
    message: string
    data: { updated: number }
  }>(
    `${BUSINESS_LAYER_NOTIFICATIONS}/api/v1/notifications/read-all`,
    {},
    { headers: buildHeaders() },
  )
  return data?.data ?? { updated: 0 }
}
